import { AsyncProgressStatus, ImportTypeConfig } from "domains/stock-transfer-imports/types";
import { ConfirmationStage, ImportTypeStage, PreCheckFileStage, SelectFileStage } from "./stages";
import { FC, useState, useCallback } from "react";
import { FileNameConflictDialog, FileNameConflictDialogState, ImportStage } from "domains/stock-transfer-imports/components";
import Wizard, { WizardStep } from "features/common/Wizard";

import { FileNameConflictResponse } from "components/ServerValidationErrors";
import { IntroStage } from "features/common/stages";
import localisations from "../../localisation";

type StockTransferImportFeatureProps = {
    fieldCompanyId: number;
};

const StockTransferImportFeature: FC<StockTransferImportFeatureProps> = ({ fieldCompanyId }) => {
    const { intro, selectImportType, selectFile, preCheckFile, confirmation } =
        localisations.stockTransferImport.stepper;
    const [preCheckStatus, setPreCheckStatus] = useState(AsyncProgressStatus.NotSubmitted);
    const [importStatus, setImportStatus] = useState(AsyncProgressStatus.NotSubmitted);
    const [importFile, setImportFile] = useState<File | undefined>(undefined);
    const [forceUploadChoice, setForceUploadChoice] = useState<boolean>(false);
    const [fileNameConflictDialogState, setFileNameConflictDialogState] = useState<FileNameConflictDialogState>({
        showDialog: false,
        importStage: "PreCheck",
        fileName: "",
        lastImportDate: null,
    });
    const [importTypeConfig, setImportTypeConfig] = useState<ImportTypeConfig | undefined>(undefined);

    const importStageFailedWithFileNameConflict = (status: AsyncProgressStatus, error: FileNameConflictResponse): boolean =>
        status === AsyncProgressStatus.Failed && error?.status === 409;

    let childProps = {
        fieldCompanyId,
        importFile,
        importTypeConfig,
        forceUploadChoice,
    };

    const handlePreCheckFileNameConflictCallback =
        useCallback((status: AsyncProgressStatus, error: FileNameConflictResponse) => {
            if (importStageFailedWithFileNameConflict(status, error)) {
                setFileNameConflictDialogState({
                    showDialog: true,
                    importStage: "PreCheck",
                    fileName: error.lastStockTransferImport.fileName,
                    lastImportDate: error.lastStockTransferImport.createdDateTime,
                });
            }
        }, [setFileNameConflictDialogState]);

    const handleImportFileNameConflictCallback =
        useCallback((status: AsyncProgressStatus, error: FileNameConflictResponse) => {
            if (importStageFailedWithFileNameConflict(status, error)) {
                setFileNameConflictDialogState({
                    showDialog:true,
                    importStage: "Import",
                    fileName: error.lastStockTransferImport.fileName,
                    lastImportDate: error.lastStockTransferImport.createdDateTime,
                });
            }
        }, [setFileNameConflictDialogState]);

    let steps = [
        {
            label: intro,
            component: <IntroStage localisations={localisations.stockTransferImport.stages.intro} />,
        },
        {
            label: selectImportType,
            disableNextButton: !importTypeConfig,
            component: (
                <ImportTypeStage
                    selectedImportTypeConfig={importTypeConfig}
                    onImportTypeConfigSelected={setImportTypeConfig}
                />
            ),
        },
        {
            label: selectFile,
            component: <SelectFileStage handleImportFileChange={setImportFile} selectedFile={importFile} />,
            disableNextButton: !importFile,
        },
        {
            label: preCheckFile,
            component: (
                <PreCheckFileStage
                    {...childProps}
                    preCheckStatus={preCheckStatus}
                    handlePreCheckStatusChange={setPreCheckStatus}
                    handleFileNameConflict={handlePreCheckFileNameConflictCallback}
                />
            ),
            disableNextButton: preCheckStatus !== AsyncProgressStatus.Success,
        },
        {
            label: confirmation,
            component: (
                <ConfirmationStage
                    {...childProps}
                    importStatus={importStatus}
                    handleImportStatusChange={setImportStatus}
                    handleFileNameConflict={handleImportFileNameConflictCallback}
                />
            ),
            disableNextButton: importStatus !== AsyncProgressStatus.Success,
        },
    ] as WizardStep[];

    return (
        <>
            <Wizard
                fieldCompanyId={fieldCompanyId}
                steps={steps}
                resultStageLocalisations={localisations.stockTransferImport.stages.result}
                serverErrorLocalisations={localisations.stockTransferImport.serverErrors}
            />
            <FileNameConflictDialog
                showDialog={fileNameConflictDialogState.showDialog}
                importStage={fileNameConflictDialogState.importStage}
                fileName={fileNameConflictDialogState.fileName}
                lastImportDate={fileNameConflictDialogState.lastImportDate}
                handleForceUploadDecision={(forceUploadDecision: boolean, importStage: ImportStage) => {
                    setForceUploadChoice(forceUploadDecision);
                    if (importStage === "PreCheck") {
                        setPreCheckStatus(AsyncProgressStatus.NotSubmitted);
                    }
                    if (importStage === "Import") {
                        setImportStatus(AsyncProgressStatus.NotSubmitted);
                    }
                    setFileNameConflictDialogState({ showDialog: false, importStage: importStage, fileName: "", lastImportDate: null });
                }}
            />
        </>
    );
};

export default StockTransferImportFeature;
